import {
  MessageDirection,
  MessageStatus,
  ChannelType,
} from 'src/api/generated';
import { format } from 'date-fns';
import { PiChatCircle } from 'react-icons/pi';
import { StyledIcon } from 'src/modules/shared/components/atoms/icons/styled-icon';
import { WhatsAppText } from 'src/modules/shared/components/molecules/messages/whatsapp-text';
import { MessageStatusIndicator } from 'src/modules/shared/components/atoms/indicators/message-status-indicator';
import { formatFullName } from 'src/modules/shared/utils/formatting/name.util';
import { TextSkeleton } from 'src/modules/shared/components/atoms/text/text-skeleton';
import {
  useCurrentUser,
  useUser,
} from 'src/modules/shared/hooks/api/admin/users';
import { AttachmentList } from '../media/attachment-list';
import { useMessage } from '../../../hooks/api/messages';
import { useMemo } from 'react';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { generateMemoToken } from 'src/modules/shared/utils/memo.util';
import { isAnyAttachmentExpired } from 'src/modules/shared/utils/attachments/expiration.util';
import { TimelineItemActions } from './timeline-item-actions';
import { TimelineItemDetails } from './timeline-item-details';
import { TimelineItemShell } from './timeline-item-shell';
import { classes } from 'src/modules/shared/utils/styles.util';
import { Text } from 'src/modules/shared/components/atoms/text';
import { resolveVariantClasses } from 'src/modules/shared/styles/variant-classes';
import { TimelineContextBadges } from './timeline-context-badges';
import {
  getContextBorderTintClass,
  getContextOpacityClass,
} from '../../../utils/timeline/item-classes.util';

import type { TimelineMessageItemProps } from '../../../types/timeline/compact-timeline.types';

export const TimelineWhatsAppItem: React.FC<TimelineMessageItemProps> = ({
  item,
  isContinuation = false,
  isNew = false,
  contact,
  context,
}) => {
  const { t } = useI18n();
  const userId = item.userId ? parseInt(item.userId) : null;
  const userQueryEnabled = userId !== null;
  const { user, loading: userLoading } = useUser(
    userId ?? -1,
    userQueryEnabled
  );
  const { user: currentUser, loading: currentUserLoading } = useCurrentUser();
  const isOutbound = item.direction === MessageDirection.OUTBOUND;

  const isTemporaryMessage = item.id.startsWith('temp-');

  const shouldFetchMessage =
    !isTemporaryMessage &&
    Boolean(item.attachments?.length) &&
    isAnyAttachmentExpired(item.attachments);

  const { message, loading } = useMessage({ id: item.id }, shouldFetchMessage);

  const getMessageSenderName = (): string => {
    if (!isOutbound) {
      return formatFullName(
        contact?.firstName,
        contact?.lastName,
        // TODO: check when implementing i18n
        item.sender.displayName?.length ? item.sender.displayName : 'Contacto'
      );
    }

    const currentUserId = currentUser?.id ?? null;
    if (currentUserId && item.userId === String(currentUserId)) {
      // TODO: check when implementing i18n
      return 'Tú';
    }

    return user
      ? formatFullName(user.firstName, user.lastName, user.email)
      : // TODO: check when implementing i18n
        'Usuario';
  };

  const getSenderTooltip = (): string => {
    const senderName = getMessageSenderName();

    // TODO: check when implementing i18n
    return isOutbound
      ? `Enviado por ${senderName}`
      : `Mensaje de ${senderName}`;
  };

  const messageToken = generateMemoToken(message, ['id', 'attachments']);
  const itemToken = generateMemoToken(item, ['id', 'attachments']);

  const attachments = useMemo(
    () => {
      if (isTemporaryMessage) {
        return item.attachments ?? [];
      }
      return message?.attachments ?? item.attachments ?? [];
    },
    // Using pre-computed memo tokens instead of raw objects to optimize performance
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [messageToken, itemToken, isTemporaryMessage]
  );

  const alignmentClass = isOutbound ? 'ml-auto' : 'mr-auto';
  const toneClass = isOutbound
    ? resolveVariantClasses('whatsapp', 'soft')
    : 'bg-white border-neutral-100';

  const borderTintClass = getContextBorderTintClass(context);
  const opacityClass = getContextOpacityClass(context);

  const senderContent =
    userLoading || currentUserLoading ? (
      <TextSkeleton className="h-5 w-36" />
    ) : (
      <Text
        size="small"
        weight="semibold"
        color="primary"
        className="min-w-0 truncate"
      >
        {getMessageSenderName()}
      </Text>
    );

  const formattedTime = format(new Date(item.createdAt), 'HH:mm');
  const formattedFullDate = format(
    new Date(item.createdAt),
    "PP 'a las' HH:mm"
  );

  const queued =
    isTemporaryMessage &&
    item.status === MessageStatus.PENDING &&
    item.metadata?.__communicationsSendState === 'queued';
  const statusLabel = queued
    ? t('communications.messageBox.whatsapp.queued')
    : undefined;

  return (
    <TimelineItemShell
      initial={isNew ? { opacity: 0, y: 8 } : false}
      animate={{ opacity: 1, y: 0 }}
      className={classes(
        'w-fit max-w-[92%] sm:max-w-[80%] min-w-0 rounded-xl',
        alignmentClass,
        toneClass,
        borderTintClass,
        opacityClass
      )}
    >
      <TimelineItemActions
        label={`Opciones del mensaje de ${getMessageSenderName()}, ${formattedTime}`}
        menuSide={isOutbound ? 'left' : 'right'}
        menuVariant={isOutbound ? 'whatsapp' : 'neutral'}
        copyText={item.content || undefined}
        details={
          <TimelineItemDetails
            channel={ChannelType.WHATSAPP}
            senderAddress={item.sender.address}
            recipientAddress={item.recipient?.address}
            description={`${getSenderTooltip()} · ${formattedFullDate}`}
          >
            {isOutbound && (
              <MessageStatusIndicator
                status={item.status}
                errorMessage={item.errors?.[0]?.message}
                label={statusLabel}
              />
            )}
          </TimelineItemDetails>
        }
      >
        {!isContinuation && (
          <div
            title={getSenderTooltip()}
            className="flex items-center gap-1.5 min-w-0 mb-1"
          >
            <div role="img" aria-label="WhatsApp" className="shrink-0 flex">
              <StyledIcon
                icon={<PiChatCircle aria-hidden="true" />}
                variant="whatsapp"
                size="sm"
              />
            </div>
            {senderContent}
          </div>
        )}
        <div className="flex [align-items:last_baseline] gap-3 min-w-0">
          <div className="min-w-0">
            {attachments.length > 0 && (
              <AttachmentList
                attachments={attachments}
                isLoading={loading && !isTemporaryMessage}
              />
            )}
            <div
              data-timeline-anchor={`Message:${item.id}`}
              className="break-words [overflow-wrap:anywhere] whitespace-pre-wrap text-sm text-primary-900"
            >
              <WhatsAppText text={item.content || ''} />
            </div>
          </div>
          <div className="ml-auto flex items-baseline justify-end gap-1.5 shrink-0">
            <Text size="tiny" color="secondary">
              {formattedTime}
            </Text>
            {isOutbound && (
              <MessageStatusIndicator
                compact
                status={item.status}
                label={statusLabel}
                variant="whatsapp"
                className="self-center"
              />
            )}
          </div>
        </div>
        <TimelineContextBadges context={context} />
        {item.status === MessageStatus.FAILED && (
          <Text size="tiny" className="text-rose-600 break-words pt-1">
            No se pudo enviar. {item.errors?.[0]?.message}
          </Text>
        )}
      </TimelineItemActions>
    </TimelineItemShell>
  );
};
